import axios from 'axios'

import { api } from './client'
import { filenameFromContentDisposition } from '@/lib/download-blob'

/** Vụ thu hoạch (tên cũ Kiểu chè) — giá trị cố định (seed). Nhập ở phiếu tươi. */
export type TeaPattern = 'mam' | 'xuan' | 'lua'

/** Nguồn phiếu: nhân viên nhập tay, hay bộ đồng bộ SmartLab tạo. */
export type TeaPurchaseSource = 'manual' | 'smartlab'

/**
 * Một lần cân trong phiếu đồng bộ, giữ nguyên hình dạng SmartLab trả về.
 *
 * weight_l1..weight_l5 có thể null: null nghĩa là máy không báo lần cân đó,
 * khác hẳn 0 (có cân nhưng không được gì). Đừng quy null về 0 khi hiển thị.
 */
export interface SmartLabWeighing {
    id: number
    invoice_code?: string | null
    weight: number
    weight_l1?: number | null
    weight_l2?: number | null
    weight_l3?: number | null
    weight_l4?: number | null
    weight_l5?: number | null
    type_tea?: string
    tea_name?: string | null
    area_id?: string
    area_name?: string | null
    time: string
}

/** Hạng chè — giá trị cố định (seed). Nhập trên phiếu cân chè khô. */
export type TeaGrade = 'ngon' | 'uot' | 'kha' | 'kha_ngon' | 'kha_vua' | 'vua'

/**
 * TeaPurchase là phiếu cân chè TƯƠI (quantity luôn là kg tươi). Kg khô sống ở
 * DriedTeaPurchase (dried-tea-purchase-api) — một phiếu khô gộp N phiếu tươi.
 */
export interface TeaPurchase {
    id: string
    code: string
    grower_id: string
    /** Rỗng được: phiếu đồng bộ vào khi máy cân không gửi mã giống chè. */
    tea_type_id?: string | null
    purchase_date: string
    quantity: number
    tea_pattern?: TeaPattern | null
    source?: TeaPurchaseSource
    /** Chi tiết từng lần cân — chỉ phiếu nguồn SmartLab mới có. */
    smartlab_weighings?: SmartLabWeighing[] | null
    note?: string | null
    approved_at?: string | null
    approved_by?: string | null
    created_by?: string | null
    created_at: string
    updated_at: string
    // hydrated read-side
    grower_name?: string
    grower_phone?: string
    grower_address?: string
    tea_type_name?: string
    approved_by_name?: string
    created_by_name?: string
    /** Phiếu khô đã gộp phiếu này (rỗng = chưa gộp). */
    dried_tea_purchase_id?: string | null
    dried_tea_purchase_code?: string
    /**
     * Mã các phiếu nhập kho lô phân loại khai phiếu khô ở trên làm nguồn, ngăn
     * bằng ", " (rỗng = chưa dùng). Đã dùng = hết đường xoá phiếu tươi này.
     */
    dried_sorting_note_codes?: string
    /**
     * Số phiếu tươi phiếu khô ở trên đang gộp (vắng = chưa gộp). 1 = xoá phiếu
     * này thì phiếu khô bị xoá theo; >1 = phiếu chỉ được gỡ khỏi phiếu khô.
     */
    dried_source_count?: number
    /** Vùng trồng đã chọn trên phiếu (luôn là mảng). */
    areas?: TeaPurchaseArea[]
}

export interface TeaPurchaseArea {
    id: string
    code: string
    name: string
}

export interface ListTeaPurchasesParams {
    grower_id?: string
    tea_type_id?: string
    /** Lọc theo trạng thái duyệt. */
    approved?: boolean
    /** 'yes' = đã gộp vào phiếu khô; 'no' = chưa gộp (picker dialog phiếu khô). */
    merged?: 'yes' | 'no'
    search?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface CreateTeaPurchaseRequest {
    grower_id: string
    tea_type_id: string
    purchase_date?: string
    /** Kg tươi. */
    quantity: number
    /** Bắt buộc (backend chặn ErrInvalidPattern). */
    tea_pattern?: TeaPattern
    note?: string
    /** Vùng trồng của hộ (không bắt buộc). */
    area_ids?: string[]
}

export interface PatchTeaPurchaseRequest {
    grower_id?: string
    tea_type_id?: string
    purchase_date?: string
    quantity?: number
    tea_pattern?: TeaPattern
    note?: string
    /** Vắng = không sửa vùng; [] = bỏ hết vùng. */
    area_ids?: string[]
}

export const teaPurchaseApi = {
    list: (params?: ListTeaPurchasesParams) =>
        api.get<TeaPurchase[]>('/tea-purchases', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),
    get: (id: string) => api.get<TeaPurchase>(`/tea-purchases/${id}`),
    create: (data: CreateTeaPurchaseRequest) =>
        api.post<TeaPurchase>('/tea-purchases', data),
    patch: (id: string, data: PatchTeaPurchaseRequest) =>
        api.patch<TeaPurchase>(`/tea-purchases/${id}`, data),
    approve: (id: string) =>
        api.post<TeaPurchase>(`/tea-purchases/${id}/approve`, {}),
    /** Xoá phiếu nhập sai. 409 khi phiếu đã gộp vào phiếu cân chè khô. */
    delete: (id: string) => api.delete<void>(`/tea-purchases/${id}`),

    /** Download Excel "BÁO CÁO PHIẾU CÂN CHÈ" theo filter đang lọc. Bypass api
     *  wrapper (chỉ JSON) để nhận response blob trực tiếp. */
    exportExcel: async (
        params: ListTeaPurchasesParams,
    ): Promise<{ blob: Blob; filename?: string }> => {
        const res = await axios.get<Blob>('/api/v1/tea-purchases/export', {
            params,
            responseType: 'blob',
            withCredentials: true,
        })
        return {
            blob: res.data,
            filename: filenameFromContentDisposition(
                (res.headers as Record<string, string | undefined>)[
                    'content-disposition'
                ],
            ),
        }
    },
}
